import React, { useRef } from 'react'
import { DimmingProfileDraft } from './types/dimmingProfile.types'
import { ActionWrapper, PassThroughAction } from './types/presenter.types'
import { useDimmingProfileEditorPresenter } from './DimmingProfileEditorPresenter'
import { DimmingProfileEditorView } from './DimmingProfileEditorView'
import {
  dimmingProfileStrings,
  getLocale,
  Locale,
} from './i18n/dimmingProfileI18n'

interface DimmingProfileEditorProps {
  // Controlled value of the editable draft (steps + active_days + dali_address).
  value: DimmingProfileDraft
  // Emitted on every edit with the next draft.
  onChange: (next: DimmingProfileDraft) => void
  // Optional locale override (defaults to the module-level current locale).
  locale?: Locale
  // Lecture seule : champs, cases et courbe figés, aucune émission d'onChange.
  readOnly?: boolean
  // Enveloppe des boutons d'action (ex. infobulle RBAC en lecture seule).
  actionWrapper?: ActionWrapper
}

const ignoreChange = () => {}

// Native SVG step-curve dimming-profile editor. Mirrors the GridEditor
// Presenter/View architecture. Exports the profile draft JSON consumed by the
// fleet feature / DimmingProfileEntity.
export const DimmingProfileEditor: React.FC<DimmingProfileEditorProps> = ({
  value,
  onChange,
  locale,
  readOnly = false,
  actionWrapper = PassThroughAction,
}) => {
  const svgRef = useRef<SVGSVGElement>(null)
  // En lecture seule, toute édition (y compris via la courbe) est neutralisée
  // à la source, en plus des contrôles désactivés dans la vue.
  const { state, actions } = useDimmingProfileEditorPresenter({
    value,
    onChange: readOnly ? ignoreChange : onChange,
    svgRef,
  })
  const strings = dimmingProfileStrings(locale ?? getLocale())

  return (
    <DimmingProfileEditorView
      state={state}
      actions={actions}
      svgRef={svgRef}
      strings={strings}
      readOnly={readOnly}
      actionWrapper={actionWrapper}
    />
  )
}

export default DimmingProfileEditor

// 0x20 zenith calendar editor (form/table v1), saved with the same profile doc.
export { default as CalendarEditor } from './components/CalendarEditor'
export type { LinkedMinigridCoords } from './components/CalendarEditor'
// Read-only intensity-vs-time preview of the zenith calendar.
export { default as CalendarCurve } from './components/CalendarCurve'

// Re-export the public types/helpers for consumers (the fleet feature).
export * from './types/dimmingProfile.types'
export type { ActionWrapper } from './types/presenter.types'
export { parseDaliAddress, buildDaliAddress } from './utils/daliAddress'
export type { DaliAddressValue } from './utils/daliAddress'
export {
  snapTimeMinutes,
  sortSteps,
  formatMinutes,
  clampLevel,
} from './utils/stepMath'
export {
  dimmingProfileStrings,
  getLocale as getDimmingProfileLocale,
  setLocale as setDimmingProfileLocale,
} from './i18n/dimmingProfileI18n'
